import { useMemo } from 'react';

import { useTrimMaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/TrimMenu/useTrimMaterialGroups';
import { getTexturedAppearanceColorGroup } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/materialGroupUtils';
import { createEmptyFilter } from 'src/modules/designStudio/helpers/productSelection/productFilterUtils';
import type {
  ProductFilter,
  VariationColorGroup,
} from 'src/modules/designStudio/helpers/productSelection/types';
import {
  ProductCategory,
  ProductTypeLabel,
} from 'src/modules/designStudio/helpers/productSelection/types';

export const useInitialFilter = () => {
  const trimMaterialGroups = useTrimMaterialGroups();

  const initialFilter: ProductFilter = useMemo(() => {
    const filter = createEmptyFilter(ProductCategory.TRIM);

    if (!trimMaterialGroups || trimMaterialGroups.length === 0) {
      return filter;
    }

    /**
     * The feature api is returning multiple trim appearances, this is a bug, the last one is the visible one.
     */
    const visibleGroup = trimMaterialGroups[trimMaterialGroups.length - 1];

    if (visibleGroup.variation) {
      let colorGroups: VariationColorGroup[] = [];

      if (visibleGroup.variation.color) {
        /**
         * The appearance and variation colors dont match when the variation was applied through a pallete/template.
         * In this case we want to respect the color shown to the user, and use this color to base the filter selection.
         * In this case we use the appearance.color to generate the filter color option.
         */
        if (
          visibleGroup.appearance.color !== visibleGroup.variation.color.hex
        ) {
          colorGroups = [
            getTexturedAppearanceColorGroup(
              visibleGroup.appearance,
            ) as VariationColorGroup,
          ];
        } else {
          /**
           * The appearance and variation colors match because the variation was applied using the product selection menu. We use the
           * variation color group.
           */
          colorGroups = [visibleGroup.variation.color.group];
        }
      }

      return {
        ...filter,
        productType: ProductTypeLabel.TRIM,
        colorGroups,
      } as ProductFilter;
    }

    return filter;
  }, [trimMaterialGroups]);

  return initialFilter;
};
